// src/modulos/TUV/TUVChecklistPage.js

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    getFirestore,
    collection,
    addDoc,
    getDocs,
    getDoc,
    doc,
    updateDoc,
    query,
    where,
    orderBy,
    Timestamp,
    deleteDoc
} from 'firebase/firestore';
import { auth as firebaseAuth, db as firebaseDb } from '../../firebaseConfig';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from './TUVChecklistPage.module.css';

// --------------------------------------------------------------
// Firebase initialization
// --------------------------------------------------------------
const db = firebaseDb;
const auth = firebaseAuth;

// --------------------------------------------------------------
// Field Types Configuration (reused from model page)
// --------------------------------------------------------------
const FIELD_TYPES = {
    AVALIATIVE: 'avaliative',
    TEXT: 'text',
    DATE: 'date',
    REGISTRATION: 'registration',
    NUMERIC: 'numeric',
    SELECTION: 'selection',
    SPECIAL: 'special'
};

const FIELD_TYPE_LABELS = {
    [FIELD_TYPES.AVALIATIVE]: 'Avaliativo',
    [FIELD_TYPES.TEXT]: 'Texto',
    [FIELD_TYPES.DATE]: 'Data',
    [FIELD_TYPES.REGISTRATION]: 'Cadastro',
    [FIELD_TYPES.NUMERIC]: 'Numérico',
    [FIELD_TYPES.SELECTION]: 'Lista de Seleção',
    [FIELD_TYPES.SPECIAL]: 'Especial'
};

const SUB_TYPES = {
    TEXT: {
        SHORT: 'short',
        LONG: 'long'
    },
    DATE: {
        TIME_24H: 'time_24h',
        TIME_24H_S: 'time_24h_s',
        TIME_12H: 'time_12h',
        TIME_12H_S: 'time_12h_s',
        DATE_DDMMYYYY: 'date_ddmmyyyy',
        DATE_MMYYYY: 'date_mmyyyy'
    },
    REGISTRATION: {
        EMAIL: 'email',
        STATE: 'state',
        UF_CITY: 'uf_city',
        CEP: 'cep',
        PHONE: 'phone',
        CNPJ: 'cnpj',
        CPF: 'cpf',
        PLATE: 'plate',
        PLATE_MERCOSUL: 'plate_mercosul'
    },
    NUMERIC: {
        MONETARY: 'monetary',
        QUANTITY: 'quantity',
        DECIMAL: 'decimal',
        PERCENTAGE: 'percentage'
    },
    SELECTION: {
        SINGLE: 'single',
        MULTIPLE: 'multiple'
    },
    SPECIAL: {
        GPS_MANUAL: 'gps_manual',
        GPS_AUTOMATIC: 'gps_automatic',
        ATTACHMENT_AUDIO: 'attachment_audio',
        ATTACHMENT_FILE: 'attachment_file',
        ATTACHMENT_PICTURE: 'attachment_picture',
        ATTACHMENT_VIDEO: 'attachment_video'
    }
};

const SUB_TYPE_LABELS = {
    [SUB_TYPES.TEXT.SHORT]: 'Texto Curto',
    [SUB_TYPES.TEXT.LONG]: 'Texto Longo',
    [SUB_TYPES.DATE.TIME_24H]: 'Horário 24h (00:00)',
    [SUB_TYPES.DATE.TIME_24H_S]: 'Horário 24h (00:00:00)',
    [SUB_TYPES.DATE.TIME_12H]: 'Horário 12h (00:00 AM/PM)',
    [SUB_TYPES.DATE.TIME_12H_S]: 'Horário 12h (00:00:00 AM/PM)',
    [SUB_TYPES.DATE.DATE_DDMMYYYY]: 'Data (dd/mm/aaaa)',
    [SUB_TYPES.DATE.DATE_MMYYYY]: 'Data (mm/aaaa)',
    [SUB_TYPES.REGISTRATION.EMAIL]: 'Email',
    [SUB_TYPES.REGISTRATION.STATE]: 'Estado',
    [SUB_TYPES.REGISTRATION.UF_CITY]: 'UF e Cidade',
    [SUB_TYPES.REGISTRATION.CEP]: 'CEP',
    [SUB_TYPES.REGISTRATION.PHONE]: 'Telefone',
    [SUB_TYPES.REGISTRATION.CNPJ]: 'CNPJ',
    [SUB_TYPES.REGISTRATION.CPF]: 'CPF',
    [SUB_TYPES.REGISTRATION.PLATE]: 'Placa Automóvel',
    [SUB_TYPES.REGISTRATION.PLATE_MERCOSUL]: 'Placa Mercosul',
    [SUB_TYPES.NUMERIC.MONETARY]: 'Monetário',
    [SUB_TYPES.NUMERIC.QUANTITY]: 'Quantidade',
    [SUB_TYPES.NUMERIC.DECIMAL]: 'Decimal',
    [SUB_TYPES.NUMERIC.PERCENTAGE]: 'Porcentagem',
    [SUB_TYPES.SELECTION.SINGLE]: 'Seleção Única',
    [SUB_TYPES.SELECTION.MULTIPLE]: 'Seleção Múltipla',
    [SUB_TYPES.SPECIAL.GPS_MANUAL]: 'GPS Manual',
    [SUB_TYPES.SPECIAL.GPS_AUTOMATIC]: 'GPS Automático',
    [SUB_TYPES.SPECIAL.ATTACHMENT_AUDIO]: 'Anexo - Áudio',
    [SUB_TYPES.SPECIAL.ATTACHMENT_FILE]: 'Anexo - Arquivo',
    [SUB_TYPES.SPECIAL.ATTACHMENT_PICTURE]: 'Anexo - Foto',
    [SUB_TYPES.SPECIAL.ATTACHMENT_VIDEO]: 'Anexo - Vídeo'
};

// --------------------------------------------------------------
// GPS Types Configuration
// --------------------------------------------------------------
const GPS_TYPES = {
    MANUAL: 'manual',
    AUTOMATIC: 'automatic'
};

const GPS_TYPE_LABELS = {
    [GPS_TYPES.MANUAL]: 'Manual',
    [GPS_TYPES.AUTOMATIC]: 'Automático'
};

// --------------------------------------------------------------
// Attachment Types Configuration
// --------------------------------------------------------------
const ATTACHMENT_TYPES = {
    AUDIO: 'audio',
    FILE: 'file',
    PICTURE: 'picture',
    VIDEO: 'video'
};

const ATTACHMENT_TYPE_LABELS = {
    [ATTACHMENT_TYPES.AUDIO]: 'Áudio',
    [ATTACHMENT_TYPES.FILE]: 'Arquivo',
    [ATTACHMENT_TYPES.PICTURE]: 'Foto',
    [ATTACHMENT_TYPES.VIDEO]: 'Vídeo'
};

const ATTACHMENT_TYPE_ICONS = {
    [ATTACHMENT_TYPES.AUDIO]: '🎙️',
    [ATTACHMENT_TYPES.FILE]: '📄',
    [ATTACHMENT_TYPES.PICTURE]: '📷',
    [ATTACHMENT_TYPES.VIDEO]: '🎬'
};

// Attachment actions for the picker
const ATTACHMENT_ACTIONS = {
    PHOTO: 'photo',
    VIDEO: 'video',
    FILE: 'file',
    AUDIO: 'audio'
};

const ATTACHMENT_ACTION_LABELS = {
    [ATTACHMENT_ACTIONS.PHOTO]: '📷 Foto',
    [ATTACHMENT_ACTIONS.VIDEO]: '🎬 Vídeo',
    [ATTACHMENT_ACTIONS.FILE]: '📄 Arquivo',
    [ATTACHMENT_ACTIONS.AUDIO]: '🎙️ Áudio'
};

const COMPLEMENT_TYPES = {
    COMMENTARY: 'commentary',
    ACTION_PLAN: 'action_plan',
    ATTACHMENT: 'attachment',
    SIGNATURE: 'signature',
    NA: 'na'
};

const COMPLEMENT_LABELS = {
    [COMPLEMENT_TYPES.COMMENTARY]: 'Comentário',
    [COMPLEMENT_TYPES.ACTION_PLAN]: 'Plano de Ação',
    [COMPLEMENT_TYPES.ATTACHMENT]: 'Anexo',
    [COMPLEMENT_TYPES.SIGNATURE]: 'Assinatura',
    [COMPLEMENT_TYPES.NA]: 'N/A (Não se Aplica)'
};

const BRAZIL_STATES = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const CURRENCY_SYMBOLS = {
    BRL: 'R$',
    USD: 'US$',
    EUR: '€',
    GBP: '£',
    ARS: 'AR$',
    UYU: '$U',
    PYG: '₲',
    CLP: 'CL$'
};

// Field type icons
const FIELD_ICONS = {
    [FIELD_TYPES.AVALIATIVE]: '✅',
    [FIELD_TYPES.TEXT]: '📝',
    [FIELD_TYPES.DATE]: '📅',
    [FIELD_TYPES.REGISTRATION]: '📋',
    [FIELD_TYPES.NUMERIC]: '🔢',
    [FIELD_TYPES.SELECTION]: '📌',
    [FIELD_TYPES.SPECIAL]: '⭐'
};

const ITEMS_PER_PAGE = 10;

// Toast Component
const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 4000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className={`${styles.toast} ${styles[`toast${type}`]}`}>
            <span className={styles.toastIcon}>
                {type === 'Success' && '✓'}
                {type === 'Error' && '⚠️'}
                {type === 'Info' && 'ℹ️'}
                {type === 'Warning' && '!'}
            </span>
            <span className={styles.toastMessage}>{message}</span>
            <button onClick={onClose} className={styles.toastClose}>×</button>
        </div>
    );
};

// Bottom Sheet Component for Mobile
const BottomSheet = ({ isOpen, onClose, children, title, subtitle }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className={styles.bottomSheetOverlay} onClick={onClose}>
            <div className={styles.bottomSheet} onClick={(e) => e.stopPropagation()}>
                <div className={styles.bottomSheetHandle} />
                {title && (
                    <div className={styles.bottomSheetHeader}>
                        <div>
                            <h2 className={styles.bottomSheetTitle}>{title}</h2>
                            {subtitle && <p className={styles.bottomSheetSubtitle}>{subtitle}</p>}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className={styles.bottomSheetClose}
                        >
                            ×
                        </button>
                    </div>
                )}
                <div className={styles.bottomSheetContent}>
                    {children}
                </div>
            </div>
        </div>
    );
};

// Mobile Card Component for Checklist Items
const ChecklistCard = ({ checklist, onPress, onContinue, onView, onDelete, can }) => {
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const getStatusColor = () => {
        if (checklist.status === 'completed') return styles.cardStatusCompleted;
        return styles.cardStatusInProgress;
    };

    return (
        <div className={styles.mobileCard} onClick={() => onPress && onPress(checklist)}>
            <div className={styles.cardHeader}>
                <div className={styles.cardTitleSection}>
                    <span className={styles.cardNumber}>
                        #{checklist.inspectionNumber || '-'}
                    </span>
                    <span className={`${styles.cardStatus} ${getStatusColor()}`}>
                        {checklist.status === 'completed' ? 'Concluído' : 'Em Andamento'}
                    </span>
                </div>
                <span className={styles.cardDate}>{formatDate(checklist.createdAt)}</span>
            </div>

            <div className={styles.cardBody}>
                <h4 className={styles.cardTitle}>{checklist.title || 'Sem título'}</h4>
                <span className={styles.cardModel}>{checklist.modelName || 'Modelo'}</span>
            </div>

            <div className={styles.cardFooter}>
                <div className={styles.cardProgress}>
                    <div className={styles.cardProgressBar}>
                        <div
                            className={styles.cardProgressFill}
                            style={{ width: `${checklist.progress || 0}%` }}
                        />
                    </div>
                    <span className={styles.cardProgressText}>{checklist.progress || 0}%</span>
                </div>
                <div className={styles.cardActions}>
                    {checklist.status === 'in_progress' && can.edit && (
                        <button
                            className={`${styles.cardActionBtn} ${styles.cardActionContinue}`}
                            onClick={(e) => { e.stopPropagation(); onContinue && onContinue(checklist); }}
                        >
                            ▶ Continuar
                        </button>
                    )}
                    {can.view && (
                        <button
                            className={`${styles.cardActionBtn} ${styles.cardActionView}`}
                            onClick={(e) => { e.stopPropagation(); onView && onView(checklist); }}
                        >
                            👁️
                        </button>
                    )}
                    {can.delete && (
                        <button
                            className={`${styles.cardActionBtn} ${styles.cardActionDelete}`}
                            onClick={(e) => { e.stopPropagation(); onDelete && onDelete(checklist.id); }}
                        >
                            🗑️
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --------------------------------------------------------------
// Main Component
// --------------------------------------------------------------
const TUVChecklistPage = () => {
    const can = usePagePermissions('tuv_checklist');

    const [currentUser, setCurrentUser] = useState(null);
    const [models, setModels] = useState([]);
    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toasts, setToasts] = useState([]);

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Checklist fill states
    const [currentChecklistId, setCurrentChecklistId] = useState(null);
    const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [checklistTitle, setChecklistTitle] = useState('');
    const [checklistDescription, setChecklistDescription] = useState('');
    const [checklistNumber, setChecklistNumber] = useState('');
    const [animatedProgress, setAnimatedProgress] = useState(0);
    const [fieldErrors, setFieldErrors] = useState({});
    const [touchedFields, setTouchedFields] = useState({});
    const [saveStatus, setSaveStatus] = useState(null);
    const [autoSaveTimer, setAutoSaveTimer] = useState(null);
    const [selectedModelForFill, setSelectedModelForFill] = useState(null);
    const [viewMode, setViewMode] = useState('fill');

    // GPS state
    const [gpsLocation, setGpsLocation] = useState(null);
    const [gpsLoading, setGpsLoading] = useState(false);
    const [gpsError, setGpsError] = useState(null);

    // Audio recording state
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [recordingTime, setRecordingTime] = useState(0);
    const [recordingTimer, setRecordingTimer] = useState(null);
    const [currentAttachmentKey, setCurrentAttachmentKey] = useState(null);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    const formDataRef = useRef(formData);
    const currentAreaIndexRef = useRef(currentAreaIndex);

    // Sync refs with state
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        currentAreaIndexRef.current = currentAreaIndex;
    }, [currentAreaIndex]);

    // Cleanup audio recording on unmount
    useEffect(() => {
        return () => {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
                mediaRecorder.stop();
            }
            if (recordingTimer) {
                clearInterval(recordingTimer);
            }
        };
    }, [mediaRecorder, recordingTimer]);

    // Toast helper
    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 4000);
    };

    // --------------------------------------------------------------
    // Auth & Data Loading
    // --------------------------------------------------------------
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
            if (user) {
                loadModels();
                loadUserChecklists();
            } else {
                setModels([]);
                setChecklists([]);
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    const loadModels = async () => {
        try {
            const q = query(
                collection(db, 'tuv_checklist_models'),
                where('isActive', '==', true)
            );
            const querySnapshot = await getDocs(q);
            const loaded = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            loaded.sort((a, b) => {
                const dateA = a.createdAt || a.updatedAt || '';
                const dateB = b.createdAt || b.updatedAt || '';
                return dateB.localeCompare(dateA);
            });

            setModels(loaded);
        } catch (error) {
            console.error('Error loading models:', error);
            addToast('Erro ao carregar modelos: ' + error.message, 'Error');
        } finally {
            setLoading(false);
        }
    };

    const loadUserChecklists = async () => {
        if (!currentUser) return;

        setLoading(true);

        try {
            const q = query(
                collection(db, 'tuv_checklists'),
                where('createdByUid', '==', currentUser.uid),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            const loaded = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setChecklists(loaded);
        } catch (error) {
            console.error('Error loading checklists:', error);
            try {
                const q = query(
                    collection(db, 'tuv_checklists'),
                    where('createdByUid', '==', currentUser.uid)
                );
                const querySnapshot = await getDocs(q);
                const loaded = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                loaded.sort((a, b) => {
                    const dateA = a.createdAt || '';
                    const dateB = b.createdAt || '';
                    return dateB.localeCompare(dateA);
                });
                setChecklists(loaded);
            } catch (fallbackError) {
                console.error('Fallback error loading checklists:', fallbackError);
                addToast('Erro ao carregar checklists: ' + fallbackError.message, 'Error');
            }
        } finally {
            setLoading(false);
        }
    };

    // --------------------------------------------------------------
    // GPS Functions
    // --------------------------------------------------------------
    const getCurrentLocation = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocalização não suportada pelo navegador'));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        timestamp: position.timestamp
                    });
                },
                (error) => {
                    reject(new Error(`Erro ao obter localização: ${error.message}`));
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        });
    }, []);

    // --------------------------------------------------------------
    // Generate Next Inspection Number
    // --------------------------------------------------------------
    const generateNextInspectionNumber = async () => {
        try {
            const q = query(
                collection(db, 'tuv_checklists'),
                where('createdByUid', '==', currentUser.uid)
            );
            const querySnapshot = await getDocs(q);

            let maxNumber = 0;

            querySnapshot.docs.forEach(doc => {
                const data = doc.data();
                if (data.inspectionNumber) {
                    const match = data.inspectionNumber.match(/Inspeção (\d+)/);
                    if (match) {
                        const num = parseInt(match[1], 10);
                        if (num > maxNumber) {
                            maxNumber = num;
                        }
                    }
                }
            });

            const nextNumber = maxNumber + 1;
            return String(nextNumber).padStart(3, '0');
        } catch (error) {
            console.error('Error generating inspection number:', error);
            const timestamp = Date.now().toString().slice(-6);
            return String(parseInt(timestamp) % 1000).padStart(3, '0');
        }
    };

    // --------------------------------------------------------------
    // Start New Checklist
    // --------------------------------------------------------------
    const startNewChecklist = async (model) => {
        if (!currentUser) {
            addToast('Usuário não autenticado', 'Error');
            return;
        }

        setIsSubmitting(true);
        setIsModalOpen(false);

        try {
            const numberStr = await generateNextInspectionNumber();
            const inspectionNumber = `Inspeção ${numberStr}`;

            const initialFormData = {};
            if (model.areas) {
                model.areas.forEach((area, areaIndex) => {
                    if (area.items) {
                        area.items.forEach((item, itemIndex) => {
                            const key = `area_${areaIndex}_item_${itemIndex}`;
                            initialFormData[key] = getDefaultValueForField(item);
                        });
                    }
                    if (area.subAreas) {
                        area.subAreas.forEach((subArea, subIndex) => {
                            if (subArea.items) {
                                subArea.items.forEach((item, itemIndex) => {
                                    const key = `area_${areaIndex}_sub_${subIndex}_item_${itemIndex}`;
                                    initialFormData[key] = getDefaultValueForField(item);
                                });
                            }
                        });
                    }
                });
            }

            const newChecklist = {
                modelId: model.id,
                modelName: model.name,
                modelVersion: model.version || '1.0',
                title: inspectionNumber,
                inspectionNumber: inspectionNumber,
                description: '',
                areas: model.areas || [],
                formData: initialFormData,
                status: 'in_progress',
                currentAreaIndex: 0,
                progress: 0,
                completedAreas: [],
                createdBy: currentUser.displayName || currentUser.email || 'Usuário',
                createdByUid: currentUser.uid,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            const docRef = await addDoc(collection(db, 'tuv_checklists'), newChecklist);

            setCurrentChecklistId(docRef.id);
            setSelectedModelForFill(model);
            setFormData(initialFormData);
            setCurrentAreaIndex(0);
            setChecklistTitle(inspectionNumber);
            setChecklistNumber(numberStr);
            setChecklistDescription('');
            setAnimatedProgress(0);
            setViewMode('fill');
            addToast(`Checklist ${inspectionNumber} criado com sucesso!`, 'Success');

            await loadUserChecklists();
        } catch (error) {
            console.error('Error creating checklist:', error);
            addToast('Erro ao criar checklist: ' + error.message, 'Error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getDefaultValueForField = (item) => {
        if (!item) return '';

        // Check if it's a GPS field
        const isGPS = item.type === FIELD_TYPES.SPECIAL &&
            (item.subType === SUB_TYPES.SPECIAL.GPS_MANUAL || item.subType === SUB_TYPES.SPECIAL.GPS_AUTOMATIC);

        if (isGPS) {
            return null;
        }

        // Check if it's an attachment field
        const isAttachment = item.type === FIELD_TYPES.SPECIAL &&
            [SUB_TYPES.SPECIAL.ATTACHMENT_AUDIO, SUB_TYPES.SPECIAL.ATTACHMENT_FILE,
            SUB_TYPES.SPECIAL.ATTACHMENT_PICTURE, SUB_TYPES.SPECIAL.ATTACHMENT_VIDEO].includes(item.subType);

        if (isAttachment) {
            return null;
        }

        switch (item.type) {
            case FIELD_TYPES.AVALIATIVE:
                return null;
            case FIELD_TYPES.TEXT:
                return '';
            case FIELD_TYPES.DATE:
                return '';
            case FIELD_TYPES.REGISTRATION:
                return '';
            case FIELD_TYPES.NUMERIC:
                return '';
            case FIELD_TYPES.SELECTION:
                return item.subType === SUB_TYPES.SELECTION.MULTIPLE ? [] : '';
            default:
                return '';
        }
    };

    // --------------------------------------------------------------
    // View Completed Checklist
    // --------------------------------------------------------------
    const viewCompletedChecklist = async (checklist) => {
        try {
            const docRef = doc(db, 'tuv_checklists', checklist.id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const model = models.find(m => m.id === data.modelId);

                const modelData = model || {
                    id: data.modelId,
                    name: data.modelName,
                    version: data.modelVersion,
                    areas: data.areas || []
                };

                setCurrentChecklistId(checklist.id);
                setSelectedModelForFill(modelData);
                setFormData(data.formData || {});
                setCurrentAreaIndex(0);
                setChecklistTitle(data.title || '');
                setChecklistNumber(data.inspectionNumber || '');
                setChecklistDescription(data.description || '');
                setAnimatedProgress(100);
                setViewMode('view');
                setIsModalOpen(false);
            } else {
                addToast('Checklist não encontrado', 'Error');
            }
        } catch (error) {
            console.error('Error loading checklist:', error);
            addToast('Erro ao carregar checklist', 'Error');
        }
    };

    const continueChecklist = async (checklist) => {
        try {
            const docRef = doc(db, 'tuv_checklists', checklist.id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const model = models.find(m => m.id === data.modelId);

                const modelData = model || {
                    id: data.modelId,
                    name: data.modelName,
                    version: data.modelVersion,
                    areas: data.areas || []
                };

                setCurrentChecklistId(checklist.id);
                setSelectedModelForFill(modelData);
                setFormData(data.formData || {});
                setCurrentAreaIndex(data.currentAreaIndex || 0);
                setChecklistTitle(data.title || '');
                setChecklistNumber(data.inspectionNumber || '');
                setChecklistDescription(data.description || '');
                setAnimatedProgress(data.progress || 0);
                setViewMode('fill');
                setIsModalOpen(false);
            } else {
                addToast('Checklist não encontrado', 'Error');
            }
        } catch (error) {
            console.error('Error loading checklist:', error);
            addToast('Erro ao carregar checklist', 'Error');
        }
    };

    // --------------------------------------------------------------
    // Form Handlers
    // --------------------------------------------------------------
    const handleFieldChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
        setTouchedFields(prev => ({ ...prev, [key]: true }));
        clearFieldError(key);
        triggerAutoSave();
    };

    const handleAvaliativeChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
        setTouchedFields(prev => ({ ...prev, [key]: true }));
        clearFieldError(key);
        triggerAutoSave();
    };

    const handleComplementChange = (key, complementType, value) => {
        const complementKey = `${key}_${complementType}`;
        setFormData(prev => ({
            ...prev,
            [complementKey]: value
        }));
        triggerAutoSave();
    };

    const clearFieldError = (key) => {
        setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[key];
            return newErrors;
        });
    };

    // --------------------------------------------------------------
    // Auto Save
    // --------------------------------------------------------------
    const triggerAutoSave = useCallback(() => {
        if (autoSaveTimer) {
            clearTimeout(autoSaveTimer);
        }
        const timer = setTimeout(() => {
            saveChecklistProgress();
        }, 1000);
        setAutoSaveTimer(timer);
    }, [autoSaveTimer]);

    // --------------------------------------------------------------
    // Progress Calculation
    // --------------------------------------------------------------
    const isFieldFilled = (item, value) => {
        if (!item) return false;
        if (value === undefined || value === null) return false;

        // Check if it's a GPS field
        const isGPS = item.type === FIELD_TYPES.SPECIAL &&
            (item.subType === SUB_TYPES.SPECIAL.GPS_MANUAL || item.subType === SUB_TYPES.SPECIAL.GPS_AUTOMATIC);

        if (isGPS) {
            if (!value) return false;
            return value.lat && value.lat !== '' && value.lng && value.lng !== '';
        }

        // Check if it's an attachment field
        const isAttachment = item.type === FIELD_TYPES.SPECIAL &&
            [SUB_TYPES.SPECIAL.ATTACHMENT_AUDIO, SUB_TYPES.SPECIAL.ATTACHMENT_FILE,
            SUB_TYPES.SPECIAL.ATTACHMENT_PICTURE, SUB_TYPES.SPECIAL.ATTACHMENT_VIDEO].includes(item.subType);

        if (isAttachment) {
            if (!value) return false;
            if (typeof value === 'object' && value.data) return true;
            if (typeof value === 'string' && value.trim() !== '') return true;
            return false;
        }

        if (item.type === FIELD_TYPES.AVALIATIVE) {
            return value === true || value === false;
        }

        if (item.type === FIELD_TYPES.SELECTION && item.subType === SUB_TYPES.SELECTION.MULTIPLE) {
            return Array.isArray(value) && value.length > 0;
        }

        if (typeof value === 'string') {
            return value.trim() !== '';
        }

        if (typeof value === 'number') {
            return !isNaN(value);
        }

        return false;
    };

    const getFieldRequired = (item) => {
        return item.required || false;
    };

    const getAreaProgress = (areaIndex, formDataSnapshot = null) => {
        const area = selectedModelForFill?.areas?.[areaIndex];
        if (!area) return 0;

        const data = formDataSnapshot || formData;
        let totalItems = 0;
        let filledItems = 0;
        let requiredItems = 0;
        let filledRequiredItems = 0;

        if (area.items && Array.isArray(area.items)) {
            area.items.forEach((item) => {
                totalItems++;
                const key = `area_${areaIndex}_item_${area.items.indexOf(item)}`;
                const value = data[key];
                const isRequired = getFieldRequired(item);
                if (isRequired) requiredItems++;
                if (isFieldFilled(item, value)) {
                    filledItems++;
                    if (isRequired) filledRequiredItems++;
                }
            });
        }

        if (area.subAreas && Array.isArray(area.subAreas)) {
            area.subAreas.forEach((subArea, subIndex) => {
                if (subArea.items && Array.isArray(subArea.items)) {
                    subArea.items.forEach((item, itemIndex) => {
                        totalItems++;
                        const key = `area_${areaIndex}_sub_${subIndex}_item_${itemIndex}`;
                        const value = data[key];
                        const isRequired = getFieldRequired(item);
                        if (isRequired) requiredItems++;
                        if (isFieldFilled(item, value)) {
                            filledItems++;
                            if (isRequired) filledRequiredItems++;
                        }
                    });
                }
            });
        }

        let weightedFilled = filledItems;
        let weightedTotal = totalItems;
        if (requiredItems > 0) {
            weightedFilled = filledRequiredItems * 2 + (filledItems - filledRequiredItems);
            weightedTotal = requiredItems * 2 + (totalItems - requiredItems);
        }

        return weightedTotal === 0 ? 0 : Math.round((weightedFilled / weightedTotal) * 100);
    };

    const isAreaComplete = (areaIndex) => {
        return getAreaProgress(areaIndex) === 100;
    };

    const calculateTotalProgress = (formDataSnapshot = null, areasSnapshot = null) => {
        const areas = areasSnapshot || selectedModelForFill?.areas || [];
        if (areas.length === 0) return 0;
        let total = 0;
        areas.forEach((_, index) => {
            total += getAreaProgress(index, formDataSnapshot);
        });
        return areas.length === 0 ? 0 : Math.round(total / areas.length);
    };

    const getTotalProgress = () => {
        return calculateTotalProgress();
    };

    const getCurrentArea = () => {
        if (!selectedModelForFill || !selectedModelForFill.areas) return null;
        return selectedModelForFill.areas[currentAreaIndex] || null;
    };

    // --------------------------------------------------------------
    // Navigation
    // --------------------------------------------------------------
    const goToNextArea = () => {
        if (selectedModelForFill && currentAreaIndex < selectedModelForFill.areas.length - 1) {
            setCurrentAreaIndex(currentAreaIndex + 1);
            saveChecklistProgress();
        }
    };

    const goToPreviousArea = () => {
        if (currentAreaIndex > 0) {
            setCurrentAreaIndex(currentAreaIndex - 1);
            saveChecklistProgress();
        }
    };

    const goToArea = (index) => {
        setCurrentAreaIndex(index);
        saveChecklistProgress();
    };

    const saveChecklistProgress = async (showNotification = false) => {
        if (!currentChecklistId) return;

        try {
            const progress = getTotalProgress();
            const docRef = doc(db, 'tuv_checklists', currentChecklistId);
            await updateDoc(docRef, {
                formData: formDataRef.current,
                currentAreaIndex: currentAreaIndexRef.current,
                progress: progress,
                updatedAt: new Date().toISOString()
            });

            setAnimatedProgress(progress);

            if (showNotification) {
                setSaveStatus('success');
                setTimeout(() => setSaveStatus(null), 2000);
            }
        } catch (error) {
            console.error('Error saving progress:', error);
            if (showNotification) {
                setSaveStatus('error');
                setTimeout(() => setSaveStatus(null), 2000);
            }
        }
    };

    // --------------------------------------------------------------
    // Submit Checklist
    // --------------------------------------------------------------
    const submitChecklist = async () => {
        if (!currentChecklistId) return;

        let allComplete = true;
        let incompleteFields = [];
        if (selectedModelForFill && selectedModelForFill.areas) {
            selectedModelForFill.areas.forEach((area, areaIndex) => {
                if (!isAreaComplete(areaIndex)) {
                    allComplete = false;
                    if (area.items) {
                        area.items.forEach((item, itemIndex) => {
                            const key = `area_${areaIndex}_item_${itemIndex}`;
                            const value = formData[key];
                            if (getFieldRequired(item) && !isFieldFilled(item, value)) {
                                incompleteFields.push(item.title || `Item ${itemIndex + 1}`);
                                setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                            }
                        });
                    }
                    if (area.subAreas) {
                        area.subAreas.forEach((subArea, subIndex) => {
                            if (subArea.items) {
                                subArea.items.forEach((item, itemIndex) => {
                                    const key = `area_${areaIndex}_sub_${subIndex}_item_${itemIndex}`;
                                    const value = formData[key];
                                    if (getFieldRequired(item) && !isFieldFilled(item, value)) {
                                        incompleteFields.push(item.title || `Item ${itemIndex + 1}`);
                                        setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                    }
                                });
                            }
                        });
                    }
                }
            });
        }

        if (!allComplete) {
            const message = incompleteFields.length > 0
                ? `Os seguintes campos obrigatórios não foram preenchidos:\n${incompleteFields.map(f => `• ${f}`).join('\n')}\n\nDeseja finalizar mesmo assim?`
                : 'Algumas áreas não estão completamente preenchidas. Deseja finalizar mesmo assim?';
            if (!window.confirm(message)) {
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const docRef = doc(db, 'tuv_checklists', currentChecklistId);
            await updateDoc(docRef, {
                formData: formData,
                status: 'completed',
                progress: 100,
                completedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            setAnimatedProgress(100);
            addToast(`Checklist ${checklistTitle} finalizado com sucesso! 🎉`, 'Success');

            closeChecklistModal();
            await loadUserChecklists();
        } catch (error) {
            console.error('Error submitting checklist:', error);
            addToast('Erro ao finalizar checklist', 'Error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --------------------------------------------------------------
    // Close Checklist Modal
    // --------------------------------------------------------------
    const closeChecklistModal = () => {
        setCurrentChecklistId(null);
        setSelectedModelForFill(null);
        setFormData({});
        setCurrentAreaIndex(0);
        setChecklistTitle('');
        setChecklistNumber('');
        setAnimatedProgress(0);
        setViewMode('fill');
        setFieldErrors({});
        setTouchedFields({});
        setGpsLocation(null);
        setGpsError(null);
        setIsRecording(false);
        setCurrentAttachmentKey(null);
        if (autoSaveTimer) {
            clearTimeout(autoSaveTimer);
            setAutoSaveTimer(null);
        }
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
        }
        if (recordingTimer) {
            clearInterval(recordingTimer);
            setRecordingTimer(null);
        }
    };

    // --------------------------------------------------------------
    // Delete Checklist
    // --------------------------------------------------------------
    const deleteChecklist = async (checklistId) => {
        if (!window.confirm('Tem certeza que deseja excluir este checklist?')) return;

        try {
            await deleteDoc(doc(db, 'tuv_checklists', checklistId));
            addToast('Checklist excluído com sucesso!', 'Success');
            await loadUserChecklists();
        } catch (error) {
            console.error('Error deleting checklist:', error);
            addToast('Erro ao excluir checklist: ' + error.message, 'Error');
        }
    };

    // --------------------------------------------------------------
    // Field Validation
    // --------------------------------------------------------------
    const validateField = (item, key) => {
        if (!getFieldRequired(item)) return true;
        const value = formData[key];
        return isFieldFilled(item, value);
    };

    // --------------------------------------------------------------
    // Audio Recording Functions
    // --------------------------------------------------------------
    const startAudioRecording = async (key) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            const chunks = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            recorder.onstop = () => {
                const audioBlob = new Blob(chunks, { type: 'audio/webm' });
                const reader = new FileReader();
                reader.onload = (event) => {
                    const fileData = {
                        name: `audio_recording_${new Date().toISOString()}.webm`,
                        size: audioBlob.size,
                        type: 'audio/webm',
                        data: event.target.result,
                        attachmentType: ATTACHMENT_TYPES.AUDIO,
                        uploadedAt: new Date().toISOString()
                    };
                    handleFieldChange(key, fileData);
                    setAudioChunks([]);
                    setIsRecording(false);
                    setCurrentAttachmentKey(null);
                    setRecordingTime(0);
                    if (recordingTimer) {
                        clearInterval(recordingTimer);
                        setRecordingTimer(null);
                    }
                    addToast('Áudio gravado com sucesso!', 'Success');
                };
                reader.readAsDataURL(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };

            setMediaRecorder(recorder);
            setIsRecording(true);
            setCurrentAttachmentKey(key);
            recorder.start();

            // Start timer
            let seconds = 0;
            const timer = setInterval(() => {
                seconds++;
                setRecordingTime(seconds);
            }, 1000);
            setRecordingTimer(timer);

        } catch (error) {
            console.error('Error accessing microphone:', error);
            addToast('Erro ao acessar o microfone. Verifique as permissões.', 'Error');
            setIsRecording(false);
            setCurrentAttachmentKey(null);
        }
    };

    const stopAudioRecording = () => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            if (recordingTimer) {
                clearInterval(recordingTimer);
                setRecordingTimer(null);
            }
            setIsRecording(false);
            setRecordingTime(0);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // --------------------------------------------------------------
    // Render Field - Mobile Optimized
    // --------------------------------------------------------------
    const renderField = (item, key, areaIndex, subAreaIndex = null, itemIndex) => {
        if (!item) return null;

        const isViewModeLocal = viewMode === 'view';
        const value = formData[key] !== undefined ? formData[key] : getDefaultValueForField(item);
        const isRequired = item.required || false;
        const fieldIcon = FIELD_ICONS[item.type] || '📄';
        const error = fieldErrors[key] || null;
        const isFilled = isFieldFilled(item, value);

        // Check if this is a GPS field
        const isGPS = item.type === FIELD_TYPES.SPECIAL &&
            (item.subType === SUB_TYPES.SPECIAL.GPS_MANUAL || item.subType === SUB_TYPES.SPECIAL.GPS_AUTOMATIC);

        // Check if this is an attachment field
        const isAttachment = item.type === FIELD_TYPES.SPECIAL &&
            [SUB_TYPES.SPECIAL.ATTACHMENT_AUDIO, SUB_TYPES.SPECIAL.ATTACHMENT_FILE,
            SUB_TYPES.SPECIAL.ATTACHMENT_PICTURE, SUB_TYPES.SPECIAL.ATTACHMENT_VIDEO].includes(item.subType);

        const renderInput = () => {
            const commonProps = {
                className: `${styles.mobileInput} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`,
                disabled: isViewModeLocal,
                onBlur: () => {
                    if (!isViewModeLocal) {
                        setTouchedFields(prev => ({ ...prev, [key]: true }));
                        if (!validateField(item, key)) {
                            setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                        }
                    }
                }
            };

            // GPS Rendering
            if (isGPS) {
                const gpsType = item.gpsType || GPS_TYPES.MANUAL;
                const gpsValue = value || (gpsType === GPS_TYPES.AUTOMATIC ? gpsLocation : null);

                return (
                    <div className={styles.mobileGPSContainer}>
                        <div className={styles.mobileGPSHeader}>
                            <span className={styles.mobileGPSIcon}>📍</span>
                            <span className={styles.mobileGPSType}>
                                {GPS_TYPE_LABELS[gpsType] || 'GPS'}
                            </span>
                        </div>

                        {gpsType === GPS_TYPES.MANUAL ? (
                            <div className={styles.mobileGPSManual}>
                                <div className={styles.mobileGPSInputRow}>
                                    <span className={styles.mobileGPSLabel}>Latitude</span>
                                    <input
                                        type="text"
                                        className={`${styles.mobileInput} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                        value={gpsValue?.lat || ''}
                                        onChange={(e) => {
                                            if (!isViewModeLocal) {
                                                const newValue = { ...(gpsValue || {}), lat: e.target.value };
                                                handleFieldChange(key, newValue);
                                            }
                                        }}
                                        placeholder="-23.550520"
                                        disabled={isViewModeLocal}
                                    />
                                </div>
                                <div className={styles.mobileGPSInputRow}>
                                    <span className={styles.mobileGPSLabel}>Longitude</span>
                                    <input
                                        type="text"
                                        className={`${styles.mobileInput} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                        value={gpsValue?.lng || ''}
                                        onChange={(e) => {
                                            if (!isViewModeLocal) {
                                                const newValue = { ...(gpsValue || {}), lng: e.target.value };
                                                handleFieldChange(key, newValue);
                                            }
                                        }}
                                        placeholder="-46.633308"
                                        disabled={isViewModeLocal}
                                    />
                                </div>
                                {gpsValue?.accuracy && (
                                    <div className={styles.mobileGPSAccuracy}>
                                        Precisão: {Math.round(gpsValue.accuracy)}m
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Automatic GPS
                            <div className={styles.mobileGPSAutomatic}>
                                <div className={styles.mobileGPSStatus}>
                                    {gpsLoading ? (
                                        <div className={styles.mobileGPSLoading}>
                                            <span className={styles.mobileSpinner} />
                                            Obtendo localização...
                                        </div>
                                    ) : gpsError ? (
                                        <div className={styles.mobileGPSError}>
                                            ⚠️ {gpsError}
                                        </div>
                                    ) : gpsValue ? (
                                        <div className={styles.mobileGPSSuccess}>
                                            <span>✅ Localização obtida</span>
                                            <div className={styles.mobileGPSResult}>
                                                <span>Lat: {gpsValue.lat}</span>
                                                <span>Lng: {gpsValue.lng}</span>
                                                {gpsValue.accuracy && (
                                                    <span>±{Math.round(gpsValue.accuracy)}m</span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={styles.mobileGPSPlaceholder}>
                                            <span>📍 Aguardando localização...</span>
                                        </div>
                                    )}
                                </div>
                                {!isViewModeLocal && !gpsValue && (
                                    <button
                                        type="button"
                                        className={styles.mobileGPSButton}
                                        onClick={async () => {
                                            setGpsLoading(true);
                                            setGpsError(null);
                                            try {
                                                const location = await getCurrentLocation();
                                                const gpsData = {
                                                    lat: location.lat,
                                                    lng: location.lng,
                                                    accuracy: location.accuracy,
                                                    timestamp: location.timestamp
                                                };
                                                handleFieldChange(key, gpsData);
                                                setGpsLocation(gpsData);
                                            } catch (err) {
                                                setGpsError(err.message);
                                            } finally {
                                                setGpsLoading(false);
                                            }
                                        }}
                                        disabled={gpsLoading}
                                    >
                                        {gpsLoading ? '⏳' : '📡 Capturar Localização'}
                                    </button>
                                )}
                                {!isViewModeLocal && gpsValue && (
                                    <button
                                        type="button"
                                        className={styles.mobileGPSRefreshBtn}
                                        onClick={async () => {
                                            setGpsLoading(true);
                                            setGpsError(null);
                                            try {
                                                const location = await getCurrentLocation();
                                                const gpsData = {
                                                    lat: location.lat,
                                                    lng: location.lng,
                                                    accuracy: location.accuracy,
                                                    timestamp: location.timestamp
                                                };
                                                handleFieldChange(key, gpsData);
                                                setGpsLocation(gpsData);
                                            } catch (err) {
                                                setGpsError(err.message);
                                            } finally {
                                                setGpsLoading(false);
                                            }
                                        }}
                                    >
                                        🔄 Atualizar
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                );
            }

            // Attachment Rendering - Enhanced
            if (isAttachment) {
                const attachmentType = item.attachmentType || ATTACHMENT_TYPES.FILE;
                const maxSize = item.maxSize || 10;
                const allowedExtensions = item.allowedExtensions?.split(',').map(ext => ext.trim()) || [];
                const fileValue = value;

                const getAcceptAttr = () => {
                    switch (attachmentType) {
                        case ATTACHMENT_TYPES.AUDIO:
                            return 'audio/*';
                        case ATTACHMENT_TYPES.PICTURE:
                            return 'image/*';
                        case ATTACHMENT_TYPES.VIDEO:
                            return 'video/*';
                        case ATTACHMENT_TYPES.FILE:
                        default:
                            return allowedExtensions.length > 0 ? allowedExtensions.join(',') : '*/*';
                    }
                };

                const getFileIcon = () => {
                    if (!fileValue) return ATTACHMENT_TYPE_ICONS[attachmentType] || '📎';
                    if (typeof fileValue === 'object' && fileValue.data) return '✅';
                    if (typeof fileValue === 'object' && fileValue.type?.startsWith('image/')) return '🖼️';
                    if (typeof fileValue === 'object' && fileValue.type?.startsWith('video/')) return '🎬';
                    if (typeof fileValue === 'object' && fileValue.type?.startsWith('audio/')) return '🎙️';
                    return '📄';
                };

                const getFileLabel = () => {
                    if (!fileValue) {
                        return `Selecionar ${ATTACHMENT_TYPE_LABELS[attachmentType]?.toLowerCase() || 'arquivo'}`;
                    }
                    if (typeof fileValue === 'object' && fileValue.name) {
                        return fileValue.name;
                    }
                    if (typeof fileValue === 'string') {
                        return fileValue;
                    }
                    return 'Arquivo selecionado';
                };

                const getFileSize = () => {
                    if (typeof fileValue === 'object' && fileValue.size) {
                        return (fileValue.size / 1024).toFixed(1) + ' KB';
                    }
                    return '';
                };

                const handleFileSelection = (file) => {
                    // Validate file size
                    const fileSizeMB = file.size / (1024 * 1024);
                    if (fileSizeMB > maxSize) {
                        addToast(
                            `Arquivo muito grande (${fileSizeMB.toFixed(1)}MB). Máximo permitido: ${maxSize}MB`,
                            'Error'
                        );
                        return;
                    }

                    // Validate extension
                    if (allowedExtensions.length > 0) {
                        const ext = '.' + file.name.split('.').pop().toLowerCase();
                        if (!allowedExtensions.includes(ext)) {
                            addToast(
                                `Extensão não permitida. Permitidas: ${allowedExtensions.join(', ')}`,
                                'Error'
                            );
                            return;
                        }
                    }

                    // Read file as base64 for storage
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        const fileData = {
                            name: file.name,
                            size: file.size,
                            type: file.type,
                            data: event.target.result,
                            attachmentType: attachmentType,
                            uploadedAt: new Date().toISOString()
                        };
                        handleFieldChange(key, fileData);
                    };
                    reader.readAsDataURL(file);
                };

                const handleAttachmentAction = (action) => {
                    if (action === ATTACHMENT_ACTIONS.AUDIO) {
                        startAudioRecording(key);
                        return;
                    }

                    const input = document.createElement('input');
                    input.type = 'file';

                    switch (action) {
                        case ATTACHMENT_ACTIONS.PHOTO:
                            input.accept = 'image/*';
                            input.capture = 'environment';
                            break;
                        case ATTACHMENT_ACTIONS.VIDEO:
                            input.accept = 'video/*';
                            input.capture = 'environment';
                            break;
                        case ATTACHMENT_ACTIONS.FILE:
                            input.accept = '*/*';
                            break;
                        default:
                            input.accept = '*/*';
                    }

                    input.onchange = (e) => {
                        const file = e.target.files[0];
                        if (file) {
                            handleFileSelection(file);
                        }
                        input.remove();
                    };

                    input.click();
                };

                // If currently recording audio, show recording UI
                if (isRecording && currentAttachmentKey === key) {
                    return (
                        <div className={styles.mobileAttachmentContainer}>
                            <div className={styles.mobileRecordingUI}>
                                <div className={styles.mobileRecordingIndicator}>
                                    <span className={styles.mobileRecordingDot}></span>
                                    <span className={styles.mobileRecordingText}>Gravando áudio...</span>
                                </div>
                                <div className={styles.mobileRecordingTimer}>
                                    🕐 {formatTime(recordingTime)}
                                </div>
                                <button
                                    type="button"
                                    className={styles.mobileRecordingStopBtn}
                                    onClick={stopAudioRecording}
                                >
                                    ⏹ Parar Gravação
                                </button>
                            </div>
                        </div>
                    );
                }

                // Show attachment options or file preview
                return (
                    <div className={styles.mobileAttachmentContainer}>
                        <div className={styles.mobileAttachmentHeader}>
                            <span className={styles.mobileAttachmentIcon}>
                                {ATTACHMENT_TYPE_ICONS[attachmentType]}
                            </span>
                            <span className={styles.mobileAttachmentType}>
                                {ATTACHMENT_TYPE_LABELS[attachmentType] || 'Anexo'}
                            </span>
                            {maxSize && (
                                <span className={styles.mobileAttachmentSize}>
                                    máx {maxSize}MB
                                </span>
                            )}
                        </div>

                        {!isViewModeLocal && !fileValue ? (
                            <>
                                <div className={styles.mobileAttachmentActions}>
                                    <button
                                        type="button"
                                        className={styles.mobileAttachmentActionBtn}
                                        onClick={() => handleAttachmentAction(ATTACHMENT_ACTIONS.PHOTO)}
                                    >
                                        📷 Foto
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.mobileAttachmentActionBtn}
                                        onClick={() => handleAttachmentAction(ATTACHMENT_ACTIONS.VIDEO)}
                                    >
                                        🎬 Vídeo
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.mobileAttachmentActionBtn}
                                        onClick={() => handleAttachmentAction(ATTACHMENT_ACTIONS.FILE)}
                                    >
                                        📄 Arquivo
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.mobileAttachmentActionBtn}
                                        onClick={() => handleAttachmentAction(ATTACHMENT_ACTIONS.AUDIO)}
                                    >
                                        🎙️ Áudio
                                    </button>
                                </div>
                                <div className={styles.mobileAttachmentHint}>
                                    Clique em uma opção para adicionar um anexo
                                </div>
                            </>
                        ) : (
                            <div className={`${styles.mobileFileWrapper} ${fileValue ? styles.mobileFileFilled : ''}`}>
                                {!isViewModeLocal ? (
                                    <>
                                        <input
                                            type="file"
                                            className={styles.mobileFileInput}
                                            accept={getAcceptAttr()}
                                            capture={attachmentType === ATTACHMENT_TYPES.PICTURE ? 'environment' :
                                                attachmentType === ATTACHMENT_TYPES.VIDEO ? 'environment' : undefined}
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    handleFileSelection(file);
                                                }
                                                e.target.value = '';
                                            }}
                                            disabled={isViewModeLocal}
                                        />
                                        <span className={styles.mobileFileIcon}>{getFileIcon()}</span>
                                        <span className={styles.mobileFileText}>
                                            {getFileLabel()}
                                        </span>
                                        {fileValue && getFileSize() && (
                                            <span className={styles.mobileFileSize}>{getFileSize()}</span>
                                        )}
                                        <button
                                            type="button"
                                            className={styles.mobileFileClearBtn}
                                            onClick={() => handleFieldChange(key, null)}
                                        >
                                            ×
                                        </button>
                                    </>
                                ) : (
                                    <div className={styles.mobileFileViewOnly}>
                                        <span className={styles.mobileFileIcon}>{getFileIcon()}</span>
                                        <span className={styles.mobileFileText}>
                                            {fileValue && typeof fileValue === 'object' && fileValue.name ? fileValue.name : 'Arquivo'}
                                        </span>
                                        {fileValue && typeof fileValue === 'object' && fileValue.size && (
                                            <span className={styles.mobileFileSize}>
                                                {(fileValue.size / 1024).toFixed(1)} KB
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {fileValue && typeof fileValue === 'object' && fileValue.data && (
                            <div className={styles.mobileAttachmentPreview}>
                                {fileValue.type?.startsWith('image/') && (
                                    <img
                                        src={fileValue.data}
                                        alt="Preview"
                                        className={styles.mobileAttachmentImage}
                                    />
                                )}
                                {fileValue.type?.startsWith('audio/') && (
                                    <audio controls className={styles.mobileAttachmentAudio}>
                                        <source src={fileValue.data} />
                                        Seu navegador não suporta áudio.
                                    </audio>
                                )}
                                {fileValue.type?.startsWith('video/') && (
                                    <video controls className={styles.mobileAttachmentVideo}>
                                        <source src={fileValue.data} />
                                        Seu navegador não suporta vídeo.
                                    </video>
                                )}
                                {!fileValue.type?.startsWith('image/') &&
                                    !fileValue.type?.startsWith('audio/') &&
                                    !fileValue.type?.startsWith('video/') && (
                                        <div className={styles.mobileAttachmentFileInfo}>
                                            <span>📄 {fileValue.name || 'Arquivo'}</span>
                                            <span className={styles.mobileAttachmentSizeInfo}>
                                                {(fileValue.size / 1024).toFixed(1)} KB
                                            </span>
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                );
            }

            // Existing field types
            switch (item.type) {
                case FIELD_TYPES.AVALIATIVE:
                    return (
                        <div className={styles.mobileAvaliative}>
                            <button
                                type="button"
                                className={`${styles.mobileAvaliativeBtn} ${value === true ? styles.mobileAvaliativeYesActive : styles.mobileAvaliativeYes}`}
                                onClick={() => !isViewModeLocal && handleAvaliativeChange(key, true)}
                                disabled={isViewModeLocal}
                            >
                                <span className={styles.mobileAvaliativeIcon}>✓</span>
                                Sim
                            </button>
                            <button
                                type="button"
                                className={`${styles.mobileAvaliativeBtn} ${value === false ? styles.mobileAvaliativeNoActive : styles.mobileAvaliativeNo}`}
                                onClick={() => !isViewModeLocal && handleAvaliativeChange(key, false)}
                                disabled={isViewModeLocal}
                            >
                                <span className={styles.mobileAvaliativeIcon}>✕</span>
                                Não
                            </button>
                            {!isViewModeLocal && value !== null && value !== undefined && (
                                <button
                                    type="button"
                                    className={styles.mobileAvaliativeClear}
                                    onClick={() => handleAvaliativeChange(key, null)}
                                >
                                    ↺
                                </button>
                            )}
                        </div>
                    );

                case FIELD_TYPES.TEXT:
                    if (item.subType === SUB_TYPES.TEXT.LONG) {
                        return (
                            <div className={styles.mobileTextareaWrapper}>
                                <textarea
                                    className={`${styles.mobileTextarea} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                    value={value || ''}
                                    onChange={(e) => !isViewModeLocal && handleFieldChange(key, e.target.value)}
                                    placeholder={`Digite ${item.title?.toLowerCase() || ''}...`}
                                    rows={3}
                                    disabled={isViewModeLocal}
                                    onBlur={() => {
                                        if (!isViewModeLocal) {
                                            setTouchedFields(prev => ({ ...prev, [key]: true }));
                                            if (!validateField(item, key)) {
                                                setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                            }
                                        }
                                    }}
                                />
                            </div>
                        );
                    }
                    return (
                        <div className={styles.mobileInputWrapper}>
                            <input
                                type="text"
                                className={`${styles.mobileInput} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                value={value || ''}
                                onChange={(e) => !isViewModeLocal && handleFieldChange(key, e.target.value)}
                                placeholder={`Digite ${item.title?.toLowerCase() || ''}...`}
                                disabled={isViewModeLocal}
                                onBlur={() => {
                                    if (!isViewModeLocal) {
                                        setTouchedFields(prev => ({ ...prev, [key]: true }));
                                        if (!validateField(item, key)) {
                                            setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                        }
                                    }
                                }}
                            />
                            {isFilled && (
                                <span className={styles.mobileInputCheck}>✓</span>
                            )}
                        </div>
                    );

                case FIELD_TYPES.DATE:
                    const datePlaceholder = SUB_TYPE_LABELS[item.subType] || 'Data';
                    return (
                        <div className={styles.mobileInputWrapper}>
                            <input
                                type="text"
                                className={`${styles.mobileInput} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                value={value || ''}
                                onChange={(e) => !isViewModeLocal && handleFieldChange(key, e.target.value)}
                                placeholder={datePlaceholder}
                                disabled={isViewModeLocal}
                                onBlur={() => {
                                    if (!isViewModeLocal) {
                                        setTouchedFields(prev => ({ ...prev, [key]: true }));
                                        if (!validateField(item, key)) {
                                            setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                        }
                                    }
                                }}
                            />
                            <span className={styles.mobileInputIcon}>📅</span>
                        </div>
                    );

                case FIELD_TYPES.REGISTRATION:
                    const registrationPlaceholder = SUB_TYPE_LABELS[item.subType] || 'Cadastro';
                    if (item.subType === SUB_TYPES.REGISTRATION.STATE) {
                        return (
                            <div className={styles.mobileSelectWrapper}>
                                <select
                                    className={`${styles.mobileSelect} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                    value={value || ''}
                                    onChange={(e) => !isViewModeLocal && handleFieldChange(key, e.target.value)}
                                    disabled={isViewModeLocal}
                                    onBlur={() => {
                                        if (!isViewModeLocal) {
                                            setTouchedFields(prev => ({ ...prev, [key]: true }));
                                            if (!validateField(item, key)) {
                                                setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                            }
                                        }
                                    }}
                                >
                                    <option value="">Selecione um estado...</option>
                                    {BRAZIL_STATES.map(state => (
                                        <option key={state} value={state}>{state}</option>
                                    ))}
                                </select>
                                <div className={styles.mobileSelectArrow}>▾</div>
                            </div>
                        );
                    }
                    return (
                        <div className={styles.mobileInputWrapper}>
                            <input
                                type="text"
                                className={`${styles.mobileInput} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                value={value || ''}
                                onChange={(e) => !isViewModeLocal && handleFieldChange(key, e.target.value)}
                                placeholder={registrationPlaceholder}
                                disabled={isViewModeLocal}
                                onBlur={() => {
                                    if (!isViewModeLocal) {
                                        setTouchedFields(prev => ({ ...prev, [key]: true }));
                                        if (!validateField(item, key)) {
                                            setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                        }
                                    }
                                }}
                            />
                            <span className={styles.mobileInputIcon}>📋</span>
                        </div>
                    );

                case FIELD_TYPES.NUMERIC:
                    const numericPlaceholder = SUB_TYPE_LABELS[item.subType] || 'Número';
                    if (item.subType === SUB_TYPES.NUMERIC.MONETARY) {
                        const currencySymbol = CURRENCY_SYMBOLS[item.currency] || 'R$';
                        return (
                            <div className={styles.mobileCurrencyWrapper}>
                                <span className={styles.mobileCurrencySymbol}>{currencySymbol}</span>
                                <input
                                    type="text"
                                    className={`${styles.mobileCurrencyInput} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                    value={value || ''}
                                    onChange={(e) => !isViewModeLocal && handleFieldChange(key, e.target.value)}
                                    placeholder={numericPlaceholder}
                                    disabled={isViewModeLocal}
                                    onBlur={() => {
                                        if (!isViewModeLocal) {
                                            setTouchedFields(prev => ({ ...prev, [key]: true }));
                                            if (!validateField(item, key)) {
                                                setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                            }
                                        }
                                    }}
                                />
                            </div>
                        );
                    }
                    return (
                        <div className={styles.mobileInputWrapper}>
                            <input
                                type="text"
                                className={`${styles.mobileInput} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                value={value || ''}
                                onChange={(e) => !isViewModeLocal && handleFieldChange(key, e.target.value)}
                                placeholder={numericPlaceholder}
                                disabled={isViewModeLocal}
                                onBlur={() => {
                                    if (!isViewModeLocal) {
                                        setTouchedFields(prev => ({ ...prev, [key]: true }));
                                        if (!validateField(item, key)) {
                                            setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                        }
                                    }
                                }}
                            />
                            <span className={styles.mobileInputIcon}>🔢</span>
                        </div>
                    );

                case FIELD_TYPES.SELECTION:
                    if (item.subType === SUB_TYPES.SELECTION.MULTIPLE) {
                        return (
                            <div className={styles.mobileCheckboxGroup}>
                                {item.options && item.options.map((opt, idx) => {
                                    const isChecked = Array.isArray(value) && value.includes(opt.label);
                                    return (
                                        <label key={idx} className={`${styles.mobileCheckboxLabel} ${isChecked ? styles.mobileCheckboxChecked : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => {
                                                    if (!isViewModeLocal) {
                                                        const current = Array.isArray(value) ? [...value] : [];
                                                        if (isChecked) {
                                                            handleFieldChange(key, current.filter(v => v !== opt.label));
                                                        } else {
                                                            handleFieldChange(key, [...current, opt.label]);
                                                        }
                                                    }
                                                }}
                                                disabled={isViewModeLocal}
                                            />
                                            <span className={styles.mobileCheckboxMark}></span>
                                            <span className={styles.mobileCheckboxLabelText}>{opt.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        );
                    }
                    return (
                        <div className={styles.mobileSelectWrapper}>
                            <select
                                className={`${styles.mobileSelect} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                value={value || ''}
                                onChange={(e) => !isViewModeLocal && handleFieldChange(key, e.target.value)}
                                disabled={isViewModeLocal}
                                onBlur={() => {
                                    if (!isViewModeLocal) {
                                        setTouchedFields(prev => ({ ...prev, [key]: true }));
                                        if (!validateField(item, key)) {
                                            setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                        }
                                    }
                                }}
                            >
                                <option value="">Selecione...</option>
                                {item.options && item.options.map((opt, idx) => (
                                    <option key={idx} value={opt.label}>{opt.label}</option>
                                ))}
                            </select>
                            <div className={styles.mobileSelectArrow}>▾</div>
                        </div>
                    );

                default:
                    return (
                        <div className={styles.mobileInputWrapper}>
                            <input
                                type="text"
                                className={`${styles.mobileInput} ${error ? styles.mobileInputError : ''} ${isFilled ? styles.mobileInputFilled : ''}`}
                                value={value || ''}
                                onChange={(e) => !isViewModeLocal && handleFieldChange(key, e.target.value)}
                                placeholder={item.title || ''}
                                disabled={isViewModeLocal}
                                onBlur={() => {
                                    if (!isViewModeLocal) {
                                        setTouchedFields(prev => ({ ...prev, [key]: true }));
                                        if (!validateField(item, key)) {
                                            setFieldErrors(prev => ({ ...prev, [key]: 'Campo obrigatório' }));
                                        }
                                    }
                                }}
                            />
                        </div>
                    );
            }
        };

        const renderComplements = () => {
            if (!item.complements || isViewModeLocal) return null;

            const enabledComplements = Object.entries(item.complements)
                .filter(([_, comp]) => comp.enabled);

            if (enabledComplements.length === 0) return null;

            return (
                <div className={styles.mobileComplementContainer}>
                    {enabledComplements.map(([type, config]) => {
                        const compKey = `${key}_${type}`;
                        const compValue = formData[compKey] || '';
                        const label = COMPLEMENT_LABELS[type] || type;
                        const isCompFilled = type === COMPLEMENT_TYPES.NA
                            ? !!compValue
                            : typeof compValue === 'string' && compValue.trim() !== '';

                        return (
                            <div key={type} className={`${styles.mobileComplementItem} ${isCompFilled ? styles.mobileComplementFilled : ''}`}>
                                <label className={styles.mobileComplementLabel}>
                                    {label}
                                    {config.required && <span className={styles.mobileRequired}>*</span>}
                                </label>
                                {type === COMPLEMENT_TYPES.COMMENTARY && (
                                    <div className={styles.mobileInputWrapper}>
                                        <textarea
                                            className={`${styles.mobileTextarea} ${isCompFilled ? styles.mobileInputFilled : ''}`}
                                            value={compValue}
                                            onChange={(e) => handleComplementChange(key, type, e.target.value)}
                                            placeholder="Digite um comentário..."
                                            rows={2}
                                        />
                                    </div>
                                )}
                                {type === COMPLEMENT_TYPES.ACTION_PLAN && (
                                    <div className={styles.mobileInputWrapper}>
                                        <textarea
                                            className={`${styles.mobileTextarea} ${isCompFilled ? styles.mobileInputFilled : ''}`}
                                            value={compValue}
                                            onChange={(e) => handleComplementChange(key, type, e.target.value)}
                                            placeholder="Descreva o plano de ação..."
                                            rows={2}
                                        />
                                    </div>
                                )}
                                {type === COMPLEMENT_TYPES.ATTACHMENT && (
                                    <div className={`${styles.mobileFileWrapper} ${isCompFilled ? styles.mobileFileFilled : ''}`}>
                                        <input
                                            type="file"
                                            className={styles.mobileFileInput}
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    handleComplementChange(key, type, file.name);
                                                }
                                            }}
                                        />
                                        <span className={styles.mobileFileIcon}>📎</span>
                                        <span className={styles.mobileFileText}>
                                            {compValue || 'Selecionar arquivo...'}
                                        </span>
                                    </div>
                                )}
                                {type === COMPLEMENT_TYPES.SIGNATURE && (
                                    <div className={styles.mobileInputWrapper}>
                                        <input
                                            type="text"
                                            className={`${styles.mobileInput} ${isCompFilled ? styles.mobileInputFilled : ''}`}
                                            value={compValue}
                                            onChange={(e) => handleComplementChange(key, type, e.target.value)}
                                            placeholder="Digite a assinatura..."
                                        />
                                        <span className={styles.mobileInputIcon}>✍️</span>
                                    </div>
                                )}
                                {type === COMPLEMENT_TYPES.NA && (
                                    <div className={styles.mobileNAWrapper}>
                                        <label className={`${styles.mobileNACheckbox} ${isCompFilled ? styles.mobileNAChecked : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={!!compValue}
                                                onChange={(e) => handleComplementChange(key, type, e.target.checked)}
                                            />
                                            <span className={styles.mobileNACheckmark}></span>
                                            Não se aplica
                                        </label>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        };

        return (
            <div key={key} className={`${styles.mobileFieldCard} ${error ? styles.mobileFieldCardError : ''} ${isFilled ? styles.mobileFieldCardFilled : ''}`}>
                <div className={styles.mobileFieldHeader}>
                    <div className={styles.mobileFieldTitleWrapper}>
                        <span className={styles.mobileFieldIcon}>{fieldIcon}</span>
                        <span className={styles.mobileFieldTitle}>
                            {item.title || 'Item sem título'}
                            {isRequired && <span className={styles.mobileRequired}>*</span>}
                        </span>
                    </div>
                    {item.subType && (
                        <span className={styles.mobileFieldBadge}>
                            {SUB_TYPE_LABELS[item.subType] || item.subType}
                        </span>
                    )}
                </div>
                <div className={styles.mobileFieldBody}>
                    {renderInput()}
                    {error && (
                        <div className={styles.mobileFieldError}>{error}</div>
                    )}
                    {renderComplements()}
                </div>
            </div>
        );
    };

    // --------------------------------------------------------------
    // Render Model Selection Bottom Sheet
    // --------------------------------------------------------------
    const renderModelSelectionSheet = () => {
        return (
            <BottomSheet
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Selecionar Modelo"
                subtitle="Escolha um modelo para iniciar um novo checklist"
            >
                {loading ? (
                    <div className={styles.mobileLoadingState}>
                        <div className={styles.mobileSpinner} />
                        <p>Carregando modelos...</p>
                    </div>
                ) : models.length === 0 ? (
                    <div className={styles.mobileEmptyState}>
                        <div className={styles.mobileEmptyIcon}>📋</div>
                        <h3>Nenhum modelo disponível</h3>
                        <p>Entre em contato com o administrador</p>
                    </div>
                ) : (
                    <div className={styles.mobileModelList}>
                        {models.map(model => (
                            <div
                                key={model.id}
                                className={styles.mobileModelCard}
                                onClick={() => startNewChecklist(model)}
                            >
                                <div className={styles.mobileModelHeader}>
                                    <div className={styles.mobileModelIcon}>📄</div>
                                    <div className={styles.mobileModelInfo}>
                                        <h4 className={styles.mobileModelName}>{model.name}</h4>
                                        {model.category && (
                                            <span className={styles.mobileModelCategory}>{model.category}</span>
                                        )}
                                    </div>
                                </div>
                                {model.description && (
                                    <p className={styles.mobileModelDescription}>{model.description}</p>
                                )}
                                <div className={styles.mobileModelFooter}>
                                    <span className={styles.mobileModelMeta}>
                                        {model.areas?.length || 0} áreas · v{model.version || '1.0'}
                                    </span>
                                    <button
                                        className={styles.mobileStartBtn}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            startNewChecklist(model);
                                        }}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? '...' : 'Iniciar'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </BottomSheet>
        );
    };

    // --------------------------------------------------------------
    // Render Checklist Bottom Sheet
    // --------------------------------------------------------------
    const renderChecklistSheet = () => {
        if (!currentChecklistId || !selectedModelForFill) return null;

        const currentArea = getCurrentArea();
        const isViewModeLocal = viewMode === 'view';

        if (!currentArea) {
            return (
                <BottomSheet
                    isOpen={true}
                    onClose={closeChecklistModal}
                    title="Checklist"
                >
                    <div className={styles.mobileEmptyState}>
                        <div className={styles.mobileEmptyIcon}>📋</div>
                        <h3>Nenhuma área encontrada</h3>
                        <p>Este checklist não possui áreas configuradas</p>
                        <button
                            className={styles.mobileCancelBtn}
                            onClick={closeChecklistModal}
                            style={{ marginTop: '20px' }}
                        >
                            Voltar
                        </button>
                    </div>
                </BottomSheet>
            );
        }

        const progress = getTotalProgress();
        const areaProgress = getAreaProgress(currentAreaIndex);
        const isComplete = isAreaComplete(currentAreaIndex);

        return (
            <BottomSheet
                isOpen={true}
                onClose={() => {
                    if (!isViewModeLocal && !window.confirm('Tem certeza que deseja sair? Seu progresso será salvo.')) {
                        return;
                    }
                    if (!isViewModeLocal) {
                        saveChecklistProgress(true);
                    }
                    closeChecklistModal();
                    loadUserChecklists();
                }}
                title={checklistTitle}
                subtitle={`${selectedModelForFill?.name} · ${isViewModeLocal ? 'Visualização' : 'Em andamento'}`}
            >
                {/* Progress */}
                <div className={styles.mobileProgressSection}>
                    <div className={styles.mobileProgressHeader}>
                        <span className={styles.mobileProgressLabel}>Progresso Geral</span>
                        <span className={styles.mobileProgressValue}>{isViewModeLocal ? 100 : Math.round(animatedProgress)}%</span>
                    </div>
                    <div className={styles.mobileProgressBar}>
                        <div
                            className={styles.mobileProgressFill}
                            style={{ width: `${isViewModeLocal ? 100 : animatedProgress}%` }}
                        />
                    </div>
                </div>

                {/* Save Status */}
                {saveStatus && !isViewModeLocal && (
                    <div className={`${styles.mobileSaveStatus} ${styles[`mobileSaveStatus${saveStatus.charAt(0).toUpperCase() + saveStatus.slice(1)}`]}`}>
                        {saveStatus === 'success' ? '💾 Salvo!' : '❌ Erro ao salvar'}
                    </div>
                )}

                {/* Area Tabs - Horizontal Scroll */}
                <div className={styles.mobileAreaTabs}>
                    {selectedModelForFill.areas.map((area, index) => {
                        const isActive = index === currentAreaIndex;
                        const isAreaCompleteCheck = isAreaComplete(index);
                        return (
                            <button
                                key={index}
                                className={`${styles.mobileAreaTab} ${isActive ? styles.mobileAreaTabActive : ''} ${isAreaCompleteCheck ? styles.mobileAreaTabComplete : ''}`}
                                onClick={() => goToArea(index)}
                                disabled={isViewModeLocal}
                            >
                                <span className={styles.mobileAreaTabIcon}>
                                    {isAreaCompleteCheck ? '✅' : `${index + 1}`}
                                </span>
                                <span className={styles.mobileAreaTabTitle}>{area.title || `Área ${index + 1}`}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Area Content */}
                <div className={styles.mobileAreaContent}>
                    <div className={styles.mobileAreaHeader}>
                        <div className={styles.mobileAreaTitleSection}>
                            <h3 className={styles.mobileAreaTitle}>
                                {currentArea.title || `Área ${currentAreaIndex + 1}`}
                            </h3>
                            <span className={styles.mobileAreaProgressText}>
                                {isViewModeLocal ? 100 : Math.round(areaProgress)}%
                            </span>
                        </div>
                        {currentArea.description && (
                            <p className={styles.mobileAreaDescription}>{currentArea.description}</p>
                        )}
                    </div>

                    {/* Form Fields */}
                    <div className={styles.mobileFieldsContainer}>
                        {currentArea.items && currentArea.items.length > 0 && (
                            <div className={styles.mobileFieldsSection}>
                                {currentArea.items.map((item, itemIndex) => {
                                    const key = `area_${currentAreaIndex}_item_${itemIndex}`;
                                    return renderField(item, key, currentAreaIndex, null, itemIndex);
                                })}
                            </div>
                        )}

                        {currentArea.subAreas && currentArea.subAreas.length > 0 && (
                            <div className={styles.mobileSubAreasContainer}>
                                {currentArea.subAreas.map((subArea, subIndex) => (
                                    <div key={subIndex} className={styles.mobileSubAreaCard}>
                                        <div className={styles.mobileSubAreaHeader}>
                                            <span className={styles.mobileSubAreaTitle}>
                                                {subArea.title || `Subárea ${subIndex + 1}`}
                                            </span>
                                            {subArea.description && (
                                                <span className={styles.mobileSubAreaDescription}>
                                                    {subArea.description}
                                                </span>
                                            )}
                                        </div>
                                        <div className={styles.mobileSubAreaFields}>
                                            {subArea.items && subArea.items.map((item, itemIndex) => {
                                                const key = `area_${currentAreaIndex}_sub_${subIndex}_item_${itemIndex}`;
                                                return renderField(item, key, currentAreaIndex, subIndex, itemIndex);
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Navigation Buttons */}
                    {!isViewModeLocal && (
                        <div className={styles.mobileFillFooter}>
                            <button
                                className={styles.mobileNavBtn}
                                onClick={goToPreviousArea}
                                disabled={currentAreaIndex === 0}
                            >
                                ←
                            </button>
                            <div className={styles.mobileFooterCenter}>
                                <button
                                    className={styles.mobileSaveBtn}
                                    onClick={() => saveChecklistProgress(true)}
                                >
                                    💾
                                </button>
                                <span className={styles.mobileAreaIndicator}>
                                    {currentAreaIndex + 1} / {selectedModelForFill.areas.length}
                                </span>
                            </div>
                            {currentAreaIndex === selectedModelForFill.areas.length - 1 ? (
                                <button
                                    className={styles.mobileSubmitBtn}
                                    onClick={submitChecklist}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? '...' : '✓'}
                                </button>
                            ) : (
                                <button
                                    className={styles.mobileNextBtn}
                                    onClick={goToNextArea}
                                >
                                    →
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </BottomSheet>
        );
    };

    // --------------------------------------------------------------
    // Render Checklist List (Mobile Cards)
    // --------------------------------------------------------------
    const renderChecklistList = () => {
        const allChecklists = checklists;

        // Filter
        let filtered = allChecklists;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(c =>
                c.title?.toLowerCase().includes(term) ||
                c.inspectionNumber?.toLowerCase().includes(term) ||
                c.modelName?.toLowerCase().includes(term) ||
                c.createdBy?.toLowerCase().includes(term)
            );
        }

        if (filterStatus === 'completed') {
            filtered = filtered.filter(c => c.status === 'completed');
        } else if (filterStatus === 'in_progress') {
            filtered = filtered.filter(c => c.status === 'in_progress');
        }

        if (loading && checklists.length === 0) {
            return (
                <div className={styles.mobileLoadingState}>
                    <div className={styles.mobileSpinner} />
                    <p>Carregando checklists...</p>
                </div>
            );
        }

        if (filtered.length === 0) {
            return (
                <div className={styles.mobileEmptyState}>
                    <div className={styles.mobileEmptyIcon}>📋</div>
                    <h3>Nenhum checklist encontrado</h3>
                    <p>Inicie uma nova inspeção para começar</p>
                </div>
            );
        }

        return (
            <div className={styles.mobileList}>
                {filtered.map(checklist => (
                    <ChecklistCard
                        key={checklist.id}
                        checklist={checklist}
                        onContinue={continueChecklist}
                        onView={viewCompletedChecklist}
                        onDelete={deleteChecklist}
                        can={can}
                    />
                ))}
            </div>
        );
    };

    // --------------------------------------------------------------
    // Main Render
    // --------------------------------------------------------------
    const isFilling = currentChecklistId && selectedModelForFill;

    return (
        <div className={styles.mobileContainer}>
            {/* Toast System */}
            {toasts.length > 0 && (
                <div className={styles.mobileToastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Model Selection Bottom Sheet */}
            {renderModelSelectionSheet()}

            {/* Checklist Bottom Sheet */}
            {renderChecklistSheet()}

            {/* Header */}
            <header className={styles.mobileHeader}>
                <h1 className={styles.mobileHeaderTitle}>📋 Checklists</h1>
                <div className={styles.mobileHeaderActions}>
                    {isFilling && (
                        <span className={styles.mobileHeaderStatus}>
                            {Math.round(animatedProgress)}%
                        </span>
                    )}
                    {can.create && (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className={styles.mobileAddBtn}
                            disabled={isSubmitting || isFilling}
                        >
                            +
                        </button>
                    )}
                </div>
            </header>

            {/* Filters */}
            <div className={styles.mobileFilters}>
                <div className={styles.mobileSearchWrapper}>
                    <span className={styles.mobileSearchIcon}>🔍</span>
                    <input
                        type="text"
                        placeholder="Buscar checklist..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.mobileSearchInput}
                    />
                </div>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className={styles.mobileFilterSelect}
                >
                    <option value="all">Todos</option>
                    <option value="completed">Concluídos</option>
                    <option value="in_progress">Em Andamento</option>
                </select>
            </div>

            {/* Checklist List */}
            {renderChecklistList()}
        </div>
    );
};

export default TUVChecklistPage;